import * as Styles from "@universityofmaryland/web-styles-library";
import { AttributeNames } from "./names.js";
import { AttributeValues } from "./values.js";
import { devWarning } from "./errors.js";
const isAttributeTrue = ({
  element,
  attributeName,
  attributeValue,
  defaultValue = false
}) => {
  const value = element.getAttribute(attributeName);
  if (value === null) {
    return defaultValue;
  }
  if (value === "false") {
    return false;
  }
  return value === attributeValue;
};
const isAttributeNotNull = ({
  element,
  attributeName
}) => {
  const value = element.getAttribute(attributeName);
  if (value === "true") return true;
  if (value === "false") return false;
  return value !== null;
};
const deprecationWarning = ({
  element,
  attributeNameOld,
  attributeNameNew
}) => {
  devWarning(
    `UMD Web Component: ${element.nodeName} - Attribute "${attributeNameOld}" is deprecated. Use "${attributeNameNew}" instead. This attribute will be removed in version 2.0.`
  );
};
const checkDeprecatedAttribute = (props) => {
  const {
    element,
    attributeNameOld,
    attributeNameNew,
    attributeValue,
    ...rest
  } = props;
  const isDeprecatedUsed = isAttributeTrue({
    ...rest,
    element,
    attributeName: attributeNameOld,
    attributeValue
  });
  if (isDeprecatedUsed) {
    deprecationWarning(props);
    return true;
  }
  return false;
};
const createAttributeCheck = (attributeName, attributeValue, defaultValue = false) => (props) => isAttributeTrue({
  ...props,
  attributeName,
  attributeValue,
  defaultValue
});
const createValueGetter = ({
  currentName,
  deprecatedName
}) => {
  return ({ element }) => {
    if (deprecatedName) {
      const deprecatedValue = element.getAttribute(deprecatedName);
      if (deprecatedValue !== null) {
        deprecationWarning({
          element,
          attributeNameOld: deprecatedName,
          attributeNameNew: currentName
        });
        return deprecatedValue;
      }
    }
    const value = element.getAttribute(currentName);
    if (value !== null) {
      return value;
    }
    return null;
  };
};
const hasInfo = {
  gifts: (props) => isAttributeNotNull({
    ...props,
    attributeName: AttributeNames.information.gift
  }),
  search: (props) => isAttributeNotNull({
    ...props,
    attributeName: AttributeNames.information.search
  })
};
const hasDecoration = {
  line: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.option.OPTIONAL_HAS_LINE,
    attributeNameNew: AttributeNames.decoration.line,
    attributeValue: ""
  }) || isAttributeNotNull({
    ...props,
    attributeName: AttributeNames.decoration.line
  })
};
const includesFeature = {
  animation: createAttributeCheck(
    AttributeNames.feature.animation,
    AttributeValues.state.TRUE,
    true
  ),
  imageExpand: createAttributeCheck(
    AttributeNames.feature.imageExpand,
    AttributeValues.state.TRUE,
    true
  ),
  fullScreenOption: createAttributeCheck(
    AttributeNames.deprecated.feature.FULLSCREEN,
    AttributeValues.state.TRUE,
    true
  ),
  visualTime: createAttributeCheck(
    AttributeNames.deprecated.feature.SHOW_TIME,
    AttributeValues.state.TRUE,
    true
  ),
  lazyLoad: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.feed.FEED_LAZY_LOAD,
    attributeNameNew: AttributeNames.feature.lazyLoad,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.feature.lazyLoad,
    attributeValue: AttributeValues.state.TRUE
  })
};
const isData = {
  type: {
    academic: (props) => checkDeprecatedAttribute({
      ...props,
      attributeNameOld: AttributeNames.deprecated.type.TYPE,
      attributeNameNew: AttributeNames.feed.type,
      attributeValue: AttributeValues.data.type.academic
    }) || isAttributeTrue({
      ...props,
      attributeName: AttributeNames.feed.type,
      attributeValue: AttributeValues.data.type.academic
    })
  },
  union: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.feed.FEED_UNION,
    attributeNameNew: AttributeNames.feed.union,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.feed.union,
    attributeValue: AttributeValues.state.TRUE,
    defaultValue: true
  })
};
const isDisplay = {
  block: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY_TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.block
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.block
  }),
  feature: createAttributeCheck(
    AttributeNames.deprecated.display.DISPLAY,
    AttributeValues.display.feature
  ),
  featured: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.featured
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.featured
  }),
  hero: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.hero
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.hero
  }),
  list: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.list
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.list
  }),
  outline: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.type.OUTLINE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.type.OUTLINE
  }),
  overlay: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.overlay
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.overlay
  }),
  promo: createAttributeCheck(
    AttributeNames.deprecated.display.DISPLAY,
    AttributeValues.display.promo
  ),
  primary: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.type.PRIMARY
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.type.PRIMARY
  }),
  secondary: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.type.SECONDARY
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.type.SECONDARY
  }),
  short: createAttributeCheck(
    AttributeNames.display.default,
    AttributeValues.display.short
  ),
  standardCentered: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.layout.defaultCentered
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.deprecated.type.TYPE,
    attributeValue: AttributeValues.layout.defaultCentered
  }),
  standardInterior: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.layout.defaultInterior
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.deprecated.type.TYPE,
    attributeValue: AttributeValues.layout.defaultInterior
  }),
  standardInteriorCentered: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.layout.defaultInteriorCentered
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.deprecated.type.TYPE,
    attributeValue: AttributeValues.layout.defaultInteriorCentered
  }),
  statement: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.statement
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.statement
  }),
  sticky: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.sticky
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.sticky
  }),
  stacked: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.stacked
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.stacked
  }),
  stackedInteriorDeprecated: createAttributeCheck(
    AttributeNames.deprecated.type.TYPE,
    AttributeValues.layout.stackedInterior
  ),
  stackedInterior: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.layout.stackedInterior
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.layout.stackedInterior
  }),
  tabular: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.tabular
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.tabular
  }),
  mega: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.DATA_TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.mega
  }) || checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.mega
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.mega
  }),
  visual: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.DATA_TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.visual
  }) || checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.visual
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.visual
  }),
  simple: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.DATA_TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.simple
  }) || checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.type.TYPE,
    attributeNameNew: AttributeNames.display.default,
    attributeValue: AttributeValues.display.simple
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.display.default,
    attributeValue: AttributeValues.display.simple
  })
};
const isInfo = {
  admissions: createAttributeCheck(
    AttributeNames.information.admissions,
    AttributeValues.state.TRUE
  ),
  events: createAttributeCheck(
    AttributeNames.information.events,
    AttributeValues.state.TRUE
  ),
  news: createAttributeCheck(
    AttributeNames.information.news,
    AttributeValues.state.TRUE
  ),
  schools: createAttributeCheck(
    AttributeNames.information.schools,
    AttributeValues.state.TRUE
  ),
  searchDomain: createAttributeCheck(
    AttributeNames.information.searchType,
    AttributeValues.search.domain
  )
};
const isLayout = {
  alertOff: createAttributeCheck(
    AttributeNames.layout.alertOff,
    AttributeValues.state.TRUE
  ),
  alignmentRight: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.layout.LAYOUT_ALIGNMENT,
    attributeNameNew: AttributeNames.layout.alignment,
    attributeValue: AttributeValues.layout.right
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.alignment,
    attributeValue: AttributeValues.layout.right
  }),
  fixed: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.layout.LAYOUT_FIXED,
    attributeNameNew: AttributeNames.layout.fixed,
    attributeValue: ""
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.fixed,
    attributeValue: AttributeValues.state.TRUE
  }),
  fullImage: createAttributeCheck(
    AttributeNames.deprecated.type.TYPE,
    AttributeValues.layout.fullImage
  ),
  heightSmall: (props) => isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.height,
    attributeValue: AttributeValues.layout.small
  }),
  hidden: createAttributeCheck(
    AttributeNames.layout.hidden,
    AttributeValues.state.TRUE
  ),
  image: createAttributeCheck(
    AttributeNames.deprecated.type.TYPE,
    AttributeValues.display.image
  ),
  imagePositionRight: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.layout.LAYOUT_IMAGE_POSITION,
    attributeNameNew: AttributeNames.layout.imagePosition,
    attributeValue: AttributeValues.layout.right
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.imagePosition,
    attributeValue: AttributeValues.layout.right,
    defaultValue: true
  }),
  imagePositionLeft: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.layout.LAYOUT_IMAGE_POSITION,
    attributeNameNew: AttributeNames.layout.imagePosition,
    attributeValue: AttributeValues.layout.left
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.imagePosition,
    attributeValue: AttributeValues.layout.left,
    defaultValue: false
  }),
  imageScaled: (props) => isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.imageScaled,
    attributeValue: AttributeValues.state.TRUE,
    defaultValue: true
  }),
  interior: createAttributeCheck(
    AttributeNames.layout.interior,
    AttributeValues.state.TRUE
  ),
  lock: createAttributeCheck(
    AttributeNames.layout.lock,
    AttributeValues.state.TRUE
  ),
  lockFull: createAttributeCheck(
    AttributeNames.layout.lock,
    AttributeValues.layout.large
  ),
  reverse: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.layout.DEFAULT,
    attributeNameNew: AttributeNames.layout.reverse,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.reverse,
    attributeValue: AttributeValues.state.TRUE
  }),
  spaceHorizontalLarge: (props) => isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.spaceHorizontal,
    attributeValue: AttributeValues.layout.large
  }),
  textCentered: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_TEXT_CENTER,
    attributeNameNew: AttributeNames.layout.text,
    attributeValue: AttributeValues.layout.center
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.layout.text,
    attributeValue: AttributeValues.layout.center
  })
};
const isSharing = {
  email: createAttributeCheck(
    AttributeNames.sharing.email,
    AttributeValues.state.TRUE
  ),
  print: createAttributeCheck(
    AttributeNames.sharing.print,
    AttributeValues.state.TRUE
  )
};
const includesSharing = {
  facebook: createAttributeCheck(
    AttributeNames.social.facebook,
    AttributeValues.state.TRUE,
    true
  ),
  twitter: createAttributeCheck(
    AttributeNames.social.twitter,
    AttributeValues.state.TRUE,
    true
  )
};
const isTheme = {
  dark: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.theme.THEME_DEPRECATD,
    attributeNameNew: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.DARK
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.DARK
  }),
  gold: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.theme.THEME_DEPRECATD,
    attributeNameNew: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.GOLD
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.GOLD
  }),
  light: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.theme.THEME_DEPRECATD,
    attributeNameNew: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.LIGHT
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.LIGHT
  }),
  maryland: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.theme.THEME_DEPRECATD,
    attributeNameNew: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.MARYLAND
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.theme.default,
    attributeValue: AttributeValues.theme.MARYLAND
  })
};
const isVisual = {
  aligned: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_ALIGN,
    attributeNameNew: AttributeNames.visual.imageAligned,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.imageAligned,
    attributeValue: AttributeValues.state.TRUE
  }),
  bordered: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_BORDER,
    attributeNameNew: AttributeNames.visual.bordered,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.bordered,
    attributeValue: AttributeValues.state.TRUE
  }),
  campaign: createAttributeCheck(
    AttributeNames.visual.campaign,
    AttributeValues.campaign.forward
  ),
  icon_seal: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_HAS_LOGO,
    attributeNameNew: AttributeNames.visual.iconSeal,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.iconSeal,
    attributeValue: AttributeValues.state.TRUE
  }),
  open: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.state.STATE_DEPRECATD,
    attributeNameNew: AttributeNames.visual.open,
    attributeValue: AttributeValues.state.OPENED
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.open,
    attributeValue: AttributeValues.state.TRUE
  }),
  play: createAttributeCheck(
    AttributeNames.visual.play,
    AttributeValues.state.TRUE
  ),
  quote: createAttributeCheck(
    AttributeNames.deprecated.visual.VISUAL_QUOTE,
    AttributeValues.state.TRUE
  ),
  showIcon: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_ICON,
    attributeNameNew: AttributeNames.visual.icon,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.icon,
    attributeValue: AttributeValues.state.TRUE
  }),
  sizeNormal: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY_SIZE,
    attributeNameNew: AttributeNames.visual.size,
    attributeValue: AttributeValues.size.NORMAL
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.size,
    attributeValue: AttributeValues.size.NORMAL
  }),
  showTime: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.feature.SHOW_TIME,
    attributeNameNew: AttributeNames.visual.time,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.time,
    attributeValue: AttributeValues.state.TRUE,
    defaultValue: true
  }),
  sizeLarge: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY_SIZE,
    attributeNameNew: AttributeNames.visual.size,
    attributeValue: AttributeValues.size.LARGE
  }) || checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.display.DISPLAY_SIZE,
    attributeNameNew: AttributeNames.visual.size,
    attributeValue: ""
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.size,
    attributeValue: AttributeValues.size.LARGE
  }),
  stickyFirst: createAttributeCheck(
    AttributeNames.deprecated.option.OPTIONAL_STICKY_FIRST,
    AttributeValues.state.TRUE,
    true
  ),
  textToggle: createAttributeCheck(
    AttributeNames.visual.textToggle,
    AttributeValues.state.TRUE,
    true
  ),
  transparent: (props) => checkDeprecatedAttribute({
    ...props,
    attributeNameOld: AttributeNames.deprecated.visual.VISUAL_TRANSPARENT,
    attributeNameNew: AttributeNames.visual.transparent,
    attributeValue: AttributeValues.state.TRUE
  }) || isAttributeTrue({
    ...props,
    attributeName: AttributeNames.visual.transparent,
    attributeValue: AttributeValues.state.TRUE
  })
};
const getValue = {
  alertUrl: createValueGetter({
    currentName: AttributeNames.information.alertUrl
  }),
  loadingPriority: createValueGetter({
    currentName: AttributeNames.loading.priority
  }),
  daysToHide: createValueGetter({
    currentName: AttributeNames.visual.hiddenDays,
    deprecatedName: AttributeNames.deprecated.visual.VISUAL_DAYS_TO_HIDE
  }),
  feedEntryRemoveIds: createValueGetter({
    currentName: AttributeNames.information.removeIds,
    deprecatedName: AttributeNames.deprecated.feed.FEED_NOT_ENTRIES
  }),
  feedFilterIds: createValueGetter({
    currentName: AttributeNames.information.filterIds,
    deprecatedName: AttributeNames.deprecated.feed.FEED_CATEGORIES
  }),
  feedToken: createValueGetter({
    currentName: AttributeNames.information.token,
    deprecatedName: AttributeNames.deprecated.feed.FEED_TOKEN
  }),
  giftUrl: createValueGetter({
    currentName: AttributeNames.information.gift
  }),
  id: createValueGetter({
    currentName: AttributeNames.information.id
  }),
  ids: createValueGetter({
    currentName: AttributeNames.information.ids
  }),
  layoutColumnCount: createValueGetter({
    currentName: AttributeNames.layout.columnCount,
    deprecatedName: AttributeNames.deprecated.feed.FEED_COLUMN_COUNT
  }),
  layoutLock: createValueGetter({
    currentName: AttributeNames.layout.lock
  }),
  layoutRowCount: createValueGetter({
    currentName: AttributeNames.layout.rowCount,
    deprecatedName: AttributeNames.deprecated.feed.FEED_ROW_COUNT
  }),
  mediaTrained: createValueGetter({
    currentName: AttributeNames.information.mediaTrained
  }),
  title: createValueGetter({
    currentName: AttributeNames.information.title
  }),
  topPosition: createValueGetter({
    currentName: AttributeNames.layout.position,
    deprecatedName: AttributeNames.deprecated.layout.LAYOUT_STICKY_TOP
  }),
  styleProps: (props) => {
    const cssString = createValueGetter({
      ...props,
      currentName: AttributeNames.deprecated.visual.VISUAL_STYLE_PROPS
    });
    if (cssString && typeof cssString === "string") {
      return Styles.utilities.create.jss.objectFromString(cssString);
    }
  },
  url: createValueGetter({
    currentName: AttributeNames.information.url
  })
};
export {
  getValue,
  hasDecoration,
  hasInfo,
  includesFeature,
  includesSharing,
  isData,
  isDisplay,
  isInfo,
  isLayout,
  isSharing,
  isTheme,
  isVisual
};
//# sourceMappingURL=checks.js.map
